import i18n from "../i18n";
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatCurrency } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
    Users, Plus, Phone, MapPin, Edit2, Trash2, LogIn, LogOut, Calendar as CalIcon,
} from "lucide-react";
import { toast } from "sonner";
import { farmDateIso } from "@/lib/farmTime";

const STATUS_COLORS = {
    active: "bg-primary/10 text-primary border-primary/30",
    present: "bg-primary/10 text-primary border-primary/30",
    on_leave: "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30",
    absent: "bg-destructive/10 text-destructive border-destructive/30",
    late: "bg-orange-500/10 text-orange-700 dark:text-orange-400 border-orange-500/30",
    sick: "bg-purple-500/10 text-purple-700 dark:text-purple-400 border-purple-500/30",
    half_day: "bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/30",
    suspended: "bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/30",
    probation: "bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/30",
    resigned: "bg-slate-500/10 text-slate-600 border-slate-500/30",
    terminated: "bg-destructive/10 text-destructive border-destructive/30",
};

const EMPLOYMENT_STATUSES = [
    { value: "active", label: "Active" },
    { value: "probation", label: "On Probation" },
    { value: "on_leave", label: "On Leave" },
    { value: "sick", label: "Sick Leave" },
    { value: "suspended", label: "Suspended" },
    { value: "resigned", label: "Resigned" },
    { value: "terminated", label: "Terminated" },
];

const ATTENDANCE_STATUSES = [
    { value: "present", label: "Present" },
    { value: "late", label: "Late" },
    { value: "half_day", label: "Half Day" },
    { value: "absent", label: "Absent" },
    { value: "on_leave", label: "On Leave" },
    { value: "sick", label: "Sick" },
];

const ROLE_PRESETS = [
    "Farm Manager", "Livestock Supervisor", "Crops Supervisor", "Piggery Attendant",
    "Poultry Attendant", "Cattle Herder", "Feeder", "Vet Assistant", "Field Worker",
    "Irrigation Technician", "Aquaculture Attendant", "POS Cashier",
    "Admin / Accountant", "Security Guard", "Driver", "Maintenance",
];

const today = () => farmDateIso();

export default function EmployeesPage() {
    const nav = useNavigate();
    const [items, setItems] = useState([]);
    const [farm, setFarm] = useState({ currency: "PHP" });
    const [species, setSpecies] = useState([]);
    const [linkableUsers, setLinkableUsers] = useState([]);
    const [todaysAttendance, setTodaysAttendance] = useState([]);
    const [payrollSummary, setPayrollSummary] = useState({}); // employee_id -> summary
    const [dlg, setDlg] = useState(null);
    const [q, setQ] = useState("");
    const [filter, setFilter] = useState("all");

    const load = useCallback(async () => {
        const [e, f, sp, att, users] = await Promise.all([
            api.get("/employees").then(r => r.data),
            api.get("/farm").then(r => r.data),
            api.get("/species").then(r => r.data),
            api.get("/attendance/today").then(r => r.data).catch(() => []),
            api.get("/employees/linkable-users").then(r => r.data).catch(() => []),
        ]);
        setItems(e); setFarm(f); setSpecies(sp); setTodaysAttendance(att || []);
        setLinkableUsers(users || []);
        // Load current-month payroll summary for every employee (parallel)
        const summaries = await Promise.all(
            (e || []).map(emp =>
                api.get(`/employees/${emp.id}/payroll-summary`)
                    .then(r => [emp.id, r.data])
                    .catch(() => [emp.id, null])
            )
        );
        setPayrollSummary(Object.fromEntries(summaries.filter(([, v]) => v)));
    }, []);
    useEffect(() => { load(); }, [load]);

    const save = async () => {
        const body = {
            ...dlg,
            salary: Number(dlg.salary) || 0,
            performance_score: Number(dlg.performance_score) || 0,
            hours_worked_month: Number(dlg.hours_worked_month) || 0,
            tasks_completed: Number(dlg.tasks_completed) || 0,
            tasks_pending: Number(dlg.tasks_pending) || 0,
            skills: (dlg.skills_str || "").split(",").map(s => s.trim()).filter(Boolean),
            user_id: dlg.user_id || "",
        };
        delete body.skills_str;        try {
            if (dlg.id) await api.put(`/employees/${dlg.id}`, body);
            else await api.post("/employees", body);
            toast.success("Employee saved"); setDlg(null); load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        }
    };

    const del = async (e) => {
        if (!window.confirm(`Delete ${e.name}? This will also remove their attendance, documents, and training records.`)) return;
        try { await api.delete(`/employees/${e.id}/cascade`); toast.success("Deleted"); load(); }
        catch (err) { toast.error("Delete failed"); }
    };

    // --- Attendance actions ---
    const markAttendance = async (empId, status) => {
        const reason = window.prompt(`Manual Override Reason (${status}):`);
        if (!reason || !reason.trim()) {
            toast.error("Manual override reason is required");
            return;
        }
        if (!window.confirm(`Confirm manual override: set ${status.replace("_", " ")} for this employee?`)) return;
        try {
            await api.post("/attendance/mark", {
                employee_id: empId,
                status,
                confirm_override: true,
                override_reason: reason.trim(),
            });
            toast.success(`Marked ${status.replace("_", " ")}`);
            load();
        } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    };
    const checkOut = async (empId) => {
        const reason = window.prompt("Manual Override Reason (Out):");
        if (!reason || !reason.trim()) {
            toast.error("Manual override reason is required");
            return;
        }
        if (!window.confirm("Confirm manual override: mark Out for this employee?")) return;
        try {
            await api.post(`/attendance/checkout/${empId}`, {
                confirm_override: true,
                override_reason: reason.trim(),
            });
            toast.success("Checked out");
            load();
        } catch (err) { toast.error("Check-out failed"); }
    };

    const attendanceByEmp = Object.fromEntries((todaysAttendance || []).map(a => [a.employee_id, a]));

    const filtered = items.filter(e => {
        if (filter !== "all" && e.status !== filter) return false;
        if (q && !((e.name || "") + (e.position || "") + (e.department || "") + (e.employee_code || "")).toLowerCase().includes(q.toLowerCase())) return false;
        return true;
    });

    const active = items.filter(e => e.status === "active" || e.status === "probation");
    const stats = {
        total: items.length,
        active: active.length,
        leave: items.filter(e => e.status === "on_leave" || e.status === "sick").length,
        payroll_max: active.reduce((s, e) => s + Number(e.salary || 0), 0),
        payroll_earned: Object.values(payrollSummary).reduce((s, p) => s + Number(p?.gross_pay || 0), 0),
        present_today: todaysAttendance.filter(a => a.status === "present" || a.status === "late").length,
        absent_today: todaysAttendance.filter(a => a.status === "absent").length,
    };

    return (
        <div className="space-y-6" data-testid="employees-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-3">
                    <Users className="w-8 h-8 text-primary" />
                    <div>
                        <div className="tiny-label">{i18n.t("batch1.49ef7c7288c8")}</div>
                        <h1 className="text-4xl font-bold tracking-tight">{i18n.t("batch1.74961325bb2e")}</h1>
                        <p className="text-sm text-muted-foreground mt-1">{i18n.t("batch1.9364e8928195")}</p>
                    </div>
                </div>
                <Dialog open={!!dlg} onOpenChange={o => !o && setDlg(null)}>
                    <DialogTrigger asChild>
                        <Button
                            className="h-12"
                            onClick={() => setDlg({
                                employee_code: "", name: "", position: "", department: "",
                                phone: "", emergency_contact: "", address: "",
                                hire_date: today(), status: "active",
                                salary: 0, skills_str: "", assigned_areas: "",
                                assigned_species_ids: [], performance_score: 0,
                                tasks_completed: 0, tasks_pending: 0, hours_worked_month: 0, notes: "",
                                user_id: "", linked_login_account: null,
                                biometric_id: "", rfid_card: "", fingerprint_template: "", biometric_enrolled_at: "",
                            })}
                            data-testid="add-employee-btn"
                        >
                            <Plus className="w-4 h-4 mr-2" /> {i18n.t("batch1.18e10e9e589f")}
                        </Button>
                    </DialogTrigger>
                    <EmployeeDialog dlg={dlg} setDlg={setDlg} save={save} species={species} farm={farm} linkableUsers={linkableUsers} />
                </Dialog>
            </div>

            {/* Team KPI row */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <Kpi label={i18n.t("batch1.2ac82da485ed")} value={stats.total} testid="kpi-total-team" />
                <Kpi label={i18n.t("batch1.a733b809d2f1")} value={stats.active} tone="primary" testid="kpi-active" />
                <Kpi label={i18n.t("batch1.409bf9a68d39")} value={stats.leave} testid="kpi-leave" />
                <Kpi label={i18n.t("batch1.05882967d7f8")} value={stats.present_today} tone="primary" testid="kpi-present-today" />
                <Kpi label={i18n.t("batch1.9e9477eabd86")} value={stats.absent_today} tone="red" testid="kpi-absent-today" />
                <Kpi label={`Monthly Payroll (${farm.currency})`} value={formatCurrency(stats.payroll_max, farm.currency)} testid="kpi-payroll-max" raw />
            </div>

            <Card className="p-4">
                <div className="flex flex-col sm:flex-row gap-3 mb-4">
                    <Input placeholder={i18n.t("batch1.10dca9add4f6")} value={q} onChange={e => setQ(e.target.value)} className="h-12" data-testid="search-employees" />
                    <Select value={filter} onValueChange={setFilter}>
                        <SelectTrigger className="h-12 sm:w-48" data-testid="filter-employees"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">{i18n.t("batch1.9cb29e734a6c")}</SelectItem>
                            {EMPLOYMENT_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {filtered.map(e => {
                        const att = attendanceByEmp[e.id];
                        const summary = payrollSummary[e.id];
                        return (
                            <Card key={e.id} className="p-5" data-testid={`employee-card-${(e.employee_code || e.id).toLowerCase()}`}>
                                <div className="flex items-start gap-3">
                                    <div className="w-12 h-12 rounded-full bg-accent text-accent-foreground grid place-items-center font-bold text-lg">{(e.name || "?").slice(0, 1).toUpperCase()}</div>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-bold text-lg truncate">{e.name}</div>
                                        <div className="text-sm text-muted-foreground truncate">{e.position || "—"} · {e.department || "—"}</div>
                                        {e.employee_code && <div className="text-xs font-mono text-muted-foreground mt-0.5">{e.employee_code}</div>}
                                    </div>
                                    <span className={`text-xs px-2 py-1 rounded-md border h-fit ${STATUS_COLORS[e.status] || ""}`}>
                                        {(e.status || "").replace("_", " ")}
                                    </span>
                                </div>

                                <div className="mt-3 space-y-1 text-sm">
                                    {e.phone && <div className="flex items-center gap-2 text-muted-foreground"><Phone className="w-3.5 h-3.5" /> {e.phone}</div>}
                                    {e.assigned_areas && <div className="flex items-center gap-2 text-muted-foreground"><MapPin className="w-3.5 h-3.5" /> {e.assigned_areas}</div>}
                                    {e.salary > 0 && <div className="font-mono font-semibold">{formatCurrency(e.salary, farm.currency)}{i18n.t("batch1.e9e0ceb5e814")}</div>}
                                    {e.biometric_enrolled_at && (
                                        <div className="flex items-center gap-1 text-xs text-primary" data-testid={`biometric-indicator-${e.id}`}>
                                            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                                            {i18n.t("batch1.e8d8c8b178a1")}
                                            {e.rfid_card && <span className="text-muted-foreground"> {i18n.t("batch1.6865979bde8e")}</span>}
                                        </div>
                                    )}
                                </div>

                                {/* --- Today's attendance strip --- */}
                                <div className="mt-3 pt-3 border-t border-border/40 space-y-2" data-testid={`attendance-strip-${e.id}`}>
                                    <div className="flex items-center justify-between">
                                        <div className="text-xs text-muted-foreground flex items-center gap-1"><CalIcon className="w-3 h-3" />{i18n.t("batch1.24345a14377f")}</div>
                                        {att ? (
                                            <Badge className={`text-xs ${STATUS_COLORS[att.status] || ""}`} variant="outline">
                                                {(att.status || "").replace("_", " ")}
                                                {att.check_in_time ? ` · in ${att.check_in_time}` : ""}
                                                {att.check_out_time ? ` · out ${att.check_out_time}` : ""}
                                            </Badge>
                                        ) : (
                                            <Badge variant="outline" className="text-xs text-muted-foreground">{i18n.t("batch1.8c4cd1ca2a7f")}</Badge>
                                        )}
                                    </div>
                                    <div className="text-[10px] uppercase tracking-widest text-amber-700">{i18n.t("batch1.1d1a6f070303")}</div>
                                    <div className="grid grid-cols-4 gap-1">
                                        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => markAttendance(e.id, "present")} data-testid={`checkin-${e.id}`}>
                                            <LogIn className="w-3 h-3 mr-1" />{i18n.t("batch1.4e9f7a31eef4")}
                                        </Button>
                                        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => markAttendance(e.id, "late")}>
                                            {i18n.t("batch1.4310ed540c1c")}
                                        </Button>
                                        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => markAttendance(e.id, "absent")} data-testid={`markabsent-${e.id}`}>
                                            {i18n.t("batch1.e92452c8d3e2")}
                                        </Button>
                                        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => checkOut(e.id)} disabled={!att} data-testid={`checkout-${e.id}`}>
                                            <LogOut className="w-3 h-3 mr-1" />{i18n.t("batch1.220e06ba2fee")}
                                        </Button>
                                    </div>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-8 text-xs w-full"
                                        onClick={() => nav(`/attendance?tab=live&employee_id=${encodeURIComponent(e.id)}&date=${today()}`)}
                                    >
                                        {i18n.t("batch1.fbedf71452eb")}
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-8 text-xs w-full"
                                        onClick={() => nav(`/attendance/calendar?employee_id=${encodeURIComponent(e.id)}`)}
                                    >
                                        {i18n.t("batch1.957f629f801e")}
                                    </Button>
                                </div>

                                {/* --- Attendance-based payroll (this month) --- */}
                                {summary && (
                                    <div className="mt-3 pt-3 border-t border-border/40 bg-secondary/40 -mx-5 -mb-5 px-5 py-3 rounded-b-lg" data-testid={`payroll-summary-${e.id}`}>
                                        <div className="tiny-label mb-1">{i18n.t("batch1.27848f34b1ed")} {summary.period}</div>
                                        <div className="flex items-baseline justify-between">
                                            <div className="text-sm text-muted-foreground">
                                                <span className="font-mono font-semibold text-foreground">{summary.days_paid}</span> / {summary.working_days_default} {i18n.t("batch1.5548ae4f34cb")}
                                                <span className="text-xs"> @ {formatCurrency(summary.daily_rate, farm.currency)}{i18n.t("batch1.b1a5fa6c1056")}</span>
                                            </div>
                                            <div className="text-lg font-bold" data-testid={`gross-pay-${e.id}`}>{formatCurrency(summary.gross_pay, farm.currency)}</div>
                                        </div>
                                        <div className="flex gap-2 mt-1 text-[10px] text-muted-foreground">
                                            <span>{i18n.t("batch1.7d41b8a01b3b")} {summary.attendance_counts.present}</span>
                                            <span>{i18n.t("batch1.f6d5e7fd47b8")} {summary.attendance_counts.late}</span>
                                            <span>½: {summary.attendance_counts.half_day}</span>
                                            <span>{i18n.t("batch1.8f1f060b67a8")} {summary.attendance_counts.absent}</span>
                                            <span>{i18n.t("batch1.4614b24a3348")} {summary.attendance_counts.on_leave}</span>
                                        </div>
                                    </div>
                                )}

                                <div className="flex justify-end gap-1 mt-3 pt-3 border-t">
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => setDlg({
                                            ...e,
                                            user_id: e.user_id || "",
                                            linked_login_account: e.linked_login_account || null,
                                            skills_str: (e.skills || []).join(", "),
                                        })}
                                        data-testid={`edit-emp-${e.id}`}
                                    ><Edit2 className="w-3.5 h-3.5" /></Button>
                                    <Button size="sm" variant="destructive" onClick={() => del(e)} data-testid={`delete-emp-${e.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                                </div>
                            </Card>
                        );
                    })}
                    {filtered.length === 0 && (
                        <Card className="p-8 text-center col-span-full text-muted-foreground">
                            {i18n.t("batch1.63ac9ee9b150")} {q || filter !== "all" ? "match your filter" : "yet. Add your first team member!"}
                        </Card>
                    )}
                </div>
            </Card>
        </div>
    );
}

function Kpi({ label, value, tone = "slate", testid, raw }) {
    const cls = tone === "primary"
        ? "bg-primary/5 border-primary/20 text-primary"
        : tone === "red"
            ? "bg-destructive/5 border-destructive/20 text-destructive"
            : "bg-secondary border-border";
    return (
        <Card className={`p-4 border ${cls}`} data-testid={testid}>
            <div className="tiny-label">{label}</div>
            <div className={`font-bold mt-1 ${raw ? "text-lg" : "text-3xl"}`}>{value}</div>
        </Card>
    );
}

function EmployeeDialog({ dlg, setDlg, save, species, farm, linkableUsers }) {
    const [accountQuery, setAccountQuery] = useState("");
    if (!dlg) return null;
    const normalizedQuery = accountQuery.trim().toLowerCase();
    const selectableAccounts = (linkableUsers || []).filter((account) => {
        const linkedElsewhere = account.linked_employee_id && account.linked_employee_id !== dlg.id;
        if (linkedElsewhere) return false;
        if (!normalizedQuery) return true;
        return [account.name, account.email, account.role_name]
            .join(" ")
            .toLowerCase()
            .includes(normalizedQuery);
    });
    const selectedLinkedAccount = (linkableUsers || []).find((account) => account.id === (dlg.user_id || "")) || dlg.linked_login_account;
    return (
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{dlg.id ? "Edit" : "New"} {i18n.t("batch1.079711ea16f3")}</DialogTitle></DialogHeader>
            <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div><Label className="tiny-label">{i18n.t("batch1.c140767afa8d")}</Label><Input value={dlg.employee_code} onChange={e => setDlg({ ...dlg, employee_code: e.target.value })} placeholder={i18n.t("batch1.89f482351239")} className="h-12 mt-2 font-mono" data-testid="emp-code-input" /></div>
                    <div><Label className="tiny-label">{i18n.t("batch1.0756876bdffa")}</Label><Input value={dlg.name} onChange={e => setDlg({ ...dlg, name: e.target.value })} className="h-12 mt-2" required data-testid="emp-name-input" /></div>
                    <div><Label className="tiny-label">{i18n.t("batch1.2277adf26882")}</Label>
                        <Input
                            value={dlg.position} onChange={e => setDlg({ ...dlg, position: e.target.value })}
                            list="role-presets" placeholder={i18n.t("batch1.88c8d12b5663")}
                            className="h-12 mt-2" data-testid="emp-position-input"
                        />
                        <datalist id="role-presets">
                            {ROLE_PRESETS.map(r => <option key={r} value={r} />)}
                        </datalist>
                    </div>
                    <div><Label className="tiny-label">{i18n.t("batch1.db40106a4051")}</Label>
                        <Select value={dlg.department || ""} onValueChange={v => setDlg({ ...dlg, department: v })}>
                            <SelectTrigger className="h-12 mt-2" data-testid="emp-department-select"><SelectValue placeholder={i18n.t("batch1.1f77e83907c4")} /></SelectTrigger>
                            <SelectContent>
                                {["Livestock", "Poultry", "Crops", "Veterinary", "Aquaculture", "Sales", "Admin", "Maintenance", "Finance"].map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div><Label className="tiny-label">{i18n.t("batch1.77064d526523")}</Label><Input value={dlg.phone} onChange={e => setDlg({ ...dlg, phone: e.target.value })} className="h-12 mt-2" /></div>
                    <div><Label className="tiny-label">{i18n.t("batch1.0d16c6e19d1f")}</Label><Input value={dlg.emergency_contact} onChange={e => setDlg({ ...dlg, emergency_contact: e.target.value })} className="h-12 mt-2" /></div>
                    <div><Label className="tiny-label">{i18n.t("batch1.a8d919de0525")}</Label><Input type="date" value={dlg.hire_date} onChange={e => setDlg({ ...dlg, hire_date: e.target.value })} className="h-12 mt-2" /></div>
                    <div><Label className="tiny-label">{i18n.t("batch1.9845e4c7cbf9")}</Label>
                        <Select value={dlg.status} onValueChange={v => setDlg({ ...dlg, status: v })}>
                            <SelectTrigger className="h-12 mt-2" data-testid="emp-status-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {EMPLOYMENT_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div><Label className="tiny-label">{i18n.t("batch1.636e86725f30")}{farm.currency})</Label><Input type="number" value={dlg.salary} onChange={e => setDlg({ ...dlg, salary: e.target.value })} className="h-12 mt-2 font-mono" data-testid="emp-salary-input" /></div>
                    <div><Label className="tiny-label">{i18n.t("batch1.4dfaf7a86dc1")}</Label><Input value={dlg.assigned_areas} onChange={e => setDlg({ ...dlg, assigned_areas: e.target.value })} placeholder={i18n.t("batch1.f7fc105d02b9")} className="h-12 mt-2" /></div>
                    <div className="sm:col-span-2 border rounded-xl p-4 bg-secondary/30" data-testid="linked-login-section">
                        <div className="text-sm font-semibold">{i18n.t("batch1.5b4a3dbd3cca")}</div>
                        <div className="text-xs text-muted-foreground mt-1">{i18n.t("batch1.e2ba856d207e")}</div>
                        <Input
                            value={accountQuery}
                            onChange={(e) => setAccountQuery(e.target.value)}
                            placeholder={i18n.t("batch1.de46fd99bf8c")}
                            className="h-10 mt-3"
                            data-testid="linked-account-search"
                        />
                        <Select
                            value={dlg.user_id || "__none__"}
                            onValueChange={(value) => setDlg({ ...dlg, user_id: value === "__none__" ? "" : value })}
                        >
                            <SelectTrigger className="h-12 mt-2" data-testid="linked-account-select">
                                <SelectValue placeholder={i18n.t("batch1.cc7255c7835c")} />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__none__">{i18n.t("batch1.661b80d237e8")}</SelectItem>
                                {selectableAccounts.map((account) => (
                                    <SelectItem key={account.id} value={account.id}>
                                        {`${account.name || "No Name"} · ${account.email} · ${account.role_name || "User"}`}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {selectedLinkedAccount?.email ? (
                            <div className="text-xs text-muted-foreground mt-2" data-testid="linked-account-preview">
                                {i18n.t("batch1.d3851cd5a3ba")} {selectedLinkedAccount.name || "No Name"} ({selectedLinkedAccount.email}) · {selectedLinkedAccount.role_name || "User"}
                            </div>
                        ) : (
                            <div className="text-xs text-muted-foreground mt-2">{i18n.t("batch1.77b373b71b38")}</div>
                        )}
                    </div>
                    <div className="sm:col-span-2"><Label className="tiny-label">{i18n.t("batch1.8d669b0d0501")}</Label><Input value={dlg.skills_str} onChange={e => setDlg({ ...dlg, skills_str: e.target.value })} placeholder={i18n.t("batch1.b717b37f7007")} className="h-12 mt-2" /></div>
                    <div><Label className="tiny-label">{i18n.t("batch1.1d55271669a9")}</Label><Input type="number" min="0" max="100" value={dlg.performance_score} onChange={e => setDlg({ ...dlg, performance_score: e.target.value })} className="h-12 mt-2 font-mono" data-testid="emp-performance-input" /></div>
                    <div><Label className="tiny-label">{i18n.t("batch1.be064f5f0956")}</Label><Input type="number" value={dlg.hours_worked_month} onChange={e => setDlg({ ...dlg, hours_worked_month: e.target.value })} className="h-12 mt-2 font-mono" /></div>
                    <div className="sm:col-span-2"><Label className="tiny-label">{i18n.t("batch1.d70f93df5e8f")}</Label><Textarea value={dlg.address} onChange={e => setDlg({ ...dlg, address: e.target.value })} rows={2} /></div>
                    <div className="sm:col-span-2"><Label className="tiny-label">{i18n.t("batch1.791415f1fe3b")}</Label>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                            {species.filter(s => s.active !== false).map(s => {
                                const arr = dlg.assigned_species_ids || [];
                                const on = arr.includes(s.id);
                                return (
                                    <button
                                        key={s.id} type="button"
                                        onClick={() => setDlg({ ...dlg, assigned_species_ids: on ? arr.filter(x => x !== s.id) : [...arr, s.id] })}
                                        className={`flex items-center gap-2 p-2 rounded-xl border text-sm transition-colors ${on ? "bg-primary/10 border-primary text-primary" : "border-border hover:bg-secondary"}`}
                                    >
                                        <span className="text-lg">{s.icon}</span>{s.name}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <div className="sm:col-span-2 border rounded-xl p-4 bg-secondary/30" data-testid="biometric-section">
                        <div className="flex items-center justify-between mb-3">
                            <div>
                                <div className="text-sm font-semibold">{i18n.t("batch1.0c63e27ff3bc")}</div>
                                <div className="text-xs text-muted-foreground">{i18n.t("batch1.d69c2f962e2b")}</div>
                            </div>
                            {dlg.biometric_enrolled_at && (
                                <span className="text-xs px-2 py-1 rounded-md bg-primary/10 text-primary border border-primary/30" data-testid="biometric-enrolled-badge">
                                    {i18n.t("batch1.41d2bfcb3221")} {dlg.biometric_enrolled_at.slice(0, 10)}
                                </span>
                            )}
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <Label className="tiny-label">{i18n.t("batch1.684858dc58f7")}</Label>
                                <Input
                                    value={dlg.biometric_id || ""}
                                    onChange={e => setDlg({ ...dlg, biometric_id: e.target.value })}
                                    placeholder={i18n.t("batch1.c44471c3a3f0")}
                                    className="h-12 mt-2 font-mono"
                                    data-testid="emp-biometric-id-input"
                                />
                            </div>
                            <div>
                                <Label className="tiny-label">{i18n.t("batch1.943d3bfeae70")}</Label>
                                <Input
                                    value={dlg.rfid_card || ""}
                                    onChange={e => setDlg({ ...dlg, rfid_card: e.target.value })}
                                    placeholder={i18n.t("batch1.029b40abb2b1")}
                                    className="h-12 mt-2 font-mono"
                                    data-testid="emp-rfid-input"
                                />
                            </div>
                            <div className="sm:col-span-2">
                                <Label className="tiny-label">{i18n.t("batch1.e855dc6c9f6a")}</Label>
                                <Input
                                    value={dlg.fingerprint_template || ""}
                                    onChange={e => setDlg({ ...dlg, fingerprint_template: e.target.value })}
                                    placeholder={i18n.t("batch1.8c508c2e826b")}
                                    className="h-12 mt-2 font-mono text-xs"
                                    data-testid="emp-fingerprint-input"
                                />
                                <div className="text-[10px] text-muted-foreground mt-1">
                                    {i18n.t("batch1.ec016bec26f8")}
                                </div>
                            </div>
                            <div className="sm:col-span-2 flex gap-2">
                                <Button
                                    type="button" variant="outline" className="h-10"
                                    onClick={() => setDlg({
                                        ...dlg,
                                        biometric_enrolled_at: new Date().toISOString(),
                                    })}
                                    data-testid="mark-enrolled-btn"
                                >{i18n.t("batch1.2fdc620768b1")}</Button>
                                {dlg.biometric_enrolled_at && (
                                    <Button
                                        type="button" variant="ghost" className="h-10 text-destructive"
                                        onClick={() => setDlg({ ...dlg, biometric_id: "", rfid_card: "", fingerprint_template: "", biometric_enrolled_at: "" })}
                                        data-testid="clear-biometric-btn"
                                    >{i18n.t("batch1.08ca0722e1e6")}</Button>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="sm:col-span-2"><Label className="tiny-label">{i18n.t("batch1.70440046a3dc")}</Label><Textarea value={dlg.notes} onChange={e => setDlg({ ...dlg, notes: e.target.value })} rows={2} /></div>
                </div>
                <Button onClick={save} className="h-12 w-full" data-testid="save-employee-btn" disabled={!dlg.name?.trim()}>{i18n.t("batch1.7b156d6608a3")}</Button>
            </div>
        </DialogContent>
    );
}
